import { useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";
import {
  CalendarDays,
  Dna,
  FileText,
  FlaskConical,
  Hash,
  Plus,
  Trash2,
  UserRound,
} from "lucide-react-native";
import {
  DialogPrimaryButton,
  InlineOptionPicker,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
  SectionTitle,
  SoftSecondaryButton,
  colors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type {
  EmbryoResult,
  EmbryoSex,
  EmbryoReportFormEmbryo,
  EmbryoReportFormValues,
  ReportState,
  ReportTestType,
  TransferRecommendation,
} from "./embryoReportEntryInput";

type Props = {
  onCancel: () => void;
  onSubmit: (values: EmbryoReportFormValues) => void;
  isSubmitting: boolean;
  errorMessage?: string | null;
  initialValues?: EmbryoReportFormValues;
  mode?: "create" | "edit";
  sourceDocumentName?: string | null;
};

const EMPTY_EMBRYO: EmbryoReportFormEmbryo = {
  sampleId: "",
  embryoNumber: "",
  grade: "",
  biopsyDay: "",
  biopsyDate: "",
  karyotype: "",
  sex: "",
  result: "",
  transferRecommendation: "",
  comments: "",
};

const TEST_TYPES: { value: ReportTestType; labelKey: string }[] = [
  {
    value: "not_tested",
    labelKey: "care.embryo_report.entry.test_type_not_tested",
  },
  {
    value: "undecided",
    labelKey: "care.embryo_report.entry.test_type_undecided",
  },
  {
    value: "clinic_pending",
    labelKey: "care.embryo_report.entry.test_type_clinic_pending",
  },
  { value: "PGT-A", labelKey: "care.embryo_report.entry.test_type_pgt_a" },
  { value: "PGT-M", labelKey: "care.embryo_report.entry.test_type_pgt_m" },
  { value: "PGT-SR", labelKey: "care.embryo_report.entry.test_type_pgt_sr" },
  {
    value: "PGT-A + PGT-M",
    labelKey: "care.embryo_report.entry.test_type_pgt_a_pgt_m",
  },
  {
    value: "PGT-A + PGT-SR",
    labelKey: "care.embryo_report.entry.test_type_pgt_a_pgt_sr",
  },
  {
    value: "PGT-A + PGT-M + PGT-SR",
    labelKey: "care.embryo_report.entry.test_type_pgt_a_pgt_m_pgt_sr",
  },
  {
    value: "Whole Genome Sequencing",
    labelKey: "care.embryo_report.entry.test_type_wgs",
  },
  { value: "other", labelKey: "care.embryo_report.entry.test_type_other" },
];

const REPORT_STATES: { value: ReportState; labelKey: string }[] = [
  {
    value: "pending",
    labelKey: "care.embryo_report.entry.report_state_pending",
  },
  {
    value: "partial",
    labelKey: "care.embryo_report.entry.report_state_partial",
  },
  {
    value: "final",
    labelKey: "care.embryo_report.entry.report_state_final",
  },
];

const RESULT_OPTIONS: { value: EmbryoResult; labelKey: string }[] = [
  { value: "euploid", labelKey: "care.embryo_report.result_euploid" },
  { value: "aneuploid", labelKey: "care.embryo_report.result_aneuploid" },
  { value: "mosaic", labelKey: "care.embryo_report.result_mosaic" },
  { value: "no_result", labelKey: "care.embryo_report.result_no_result" },
];

const TRANSFER_OPTIONS: {
  value: TransferRecommendation;
  labelKey: string;
}[] = [
  { value: "yes", labelKey: "care.embryo_report.transfer_yes" },
  { value: "no", labelKey: "care.embryo_report.transfer_no" },
  {
    value: "with_counseling",
    labelKey: "care.embryo_report.transfer_counseling",
  },
];

const SEX_OPTIONS: { value: EmbryoSex; label: EmbryoSex }[] = [
  { value: "XX", label: "XX" },
  { value: "XY", label: "XY" },
];

function createEmptyValues(): EmbryoReportFormValues {
  return {
    reportState: "pending",
    reportDate: "",
    biopsyDate: "",
    retrievalDate: "",
    referringClinic: "",
    referringPhysician: "",
    laboratory: "",
    indication: "",
    testType: "undecided",
    sampleType: "",
    correctionReason: "",
    attachmentId: null,
    embryos: [],
  };
}

function FieldInput({
  value,
  onChangeText,
  placeholder,
  keyboardType = "default",
  multiline = false,
}: {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  keyboardType?: "default" | "number-pad";
  multiline?: boolean;
}) {
  return (
    <MoonFieldInput paddingY={multiline ? 10 : 0}>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.textFaint}
        keyboardType={keyboardType}
        multiline={multiline}
        style={{
          minHeight: multiline ? 72 : 44,
          paddingVertical: multiline ? 0 : 10,
          fontSize: 14,
          color: colors.text,
          textAlignVertical: multiline ? "top" : "center",
        }}
      />
    </MoonFieldInput>
  );
}

function OptionField<Value extends string>({
  label,
  value,
  options,
  onChange,
  labelIcon: Icon,
}: {
  label: string;
  value: Value | "";
  options: { value: Value; label: string }[];
  onChange: (value: Value) => void;
  labelIcon: typeof Dna;
}) {
  return (
    <MoonFormField label={label} labelIcon={Icon}>
      <InlineOptionPicker
        options={options.map((option) => ({
          id: option.value,
          label: option.label,
        }))}
        value={value}
        onChange={(next) => onChange(next as Value)}
        wrap
        columns={2}
      />
    </MoonFormField>
  );
}

export function EmbryoReportEntryForm({
  onCancel,
  onSubmit,
  isSubmitting,
  errorMessage,
  initialValues,
  mode = "create",
  sourceDocumentName,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("EmbryoReportEntryForm");
  const [values, setValues] = useState(
    () => initialValues ?? createEmptyValues(),
  );
  const isEditing = mode === "edit";

  const update = <Key extends keyof EmbryoReportFormValues>(
    key: Key,
    value: EmbryoReportFormValues[Key],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const updateEmbryo = (
    index: number,
    patch: Partial<EmbryoReportFormEmbryo>,
  ) => {
    setValues((current) => ({
      ...current,
      embryos: current.embryos.map((embryo, embryoIndex) =>
        embryoIndex === index ? { ...embryo, ...patch } : embryo,
      ),
    }));
  };

  const canSubmit =
    values.reportDate !== "" &&
    values.biopsyDate !== "" &&
    values.retrievalDate !== "" &&
    values.referringClinic.trim() !== "" &&
    values.referringPhysician.trim() !== "" &&
    values.laboratory.trim() !== "" &&
    values.indication.trim() !== "" &&
    values.sampleType.trim() !== "" &&
    (!isEditing || values.correctionReason.trim() !== "") &&
    values.embryos.every(
      (embryo) =>
        embryo.sampleId.trim() !== "" &&
        embryo.embryoNumber.trim() !== "" &&
        embryo.karyotype.trim() !== "" &&
        embryo.sex !== "" &&
        embryo.result !== "" &&
        embryo.transferRecommendation !== "",
    );

  return (
    <View style={{ gap: 18 }} testID="embryo-report-entry-form">
      <View style={{ gap: 6 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 20, fontWeight: "700", color: colors.text }}
        >
          {t(
            isEditing
              ? "care.embryo_report.entry.edit_title"
              : "care.embryo_report.entry.title",
          )}
        </Text>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 13, lineHeight: 18, color: colors.textLight }}
        >
          {t(
            isEditing
              ? "care.embryo_report.entry.edit_intro"
              : "care.embryo_report.entry.intro",
          )}
        </Text>
      </View>

      {sourceDocumentName ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 10,
            padding: 12,
            borderRadius: 14,
            backgroundColor: "rgba(124, 58, 237, 0.08)",
          }}
        >
          <FileText size={16} color={colors.warm} style={{ marginTop: 1 }} />
          <View style={{ flex: 1, gap: 3 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, fontWeight: "700", color: colors.text }}
            >
              {t("care.embryo_report.entry.source_document_ready")}
            </Text>
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{ fontSize: 12, color: colors.textLight, lineHeight: 17 }}
            >
              {sourceDocumentName}
            </Text>
          </View>
        </View>
      ) : null}

      <View style={{ gap: 14 }}>
        <SectionTitle>
          {t("care.embryo_report.entry.report_details")}
        </SectionTitle>
        {isEditing ? (
          <MoonFormField
            label={t("care.embryo_report.entry.correction_reason")}
            labelIcon={FileText}
            required
          >
            <FieldInput
              value={values.correctionReason}
              onChangeText={(value) => update("correctionReason", value)}
              placeholder={t(
                "care.embryo_report.entry.correction_reason_placeholder",
              )}
              multiline
            />
          </MoonFormField>
        ) : null}
        <MoonDateField
          label={t("care.embryo_report.report_date")}
          labelIcon={CalendarDays}
          required
          value={values.reportDate}
          onChange={(date) => update("reportDate", date)}
          placeholder={t("care.embryo_report.entry.select_date")}
        />
        <MoonDateField
          label={t("care.embryo_report.biopsy_date")}
          labelIcon={CalendarDays}
          required
          value={values.biopsyDate}
          onChange={(date) => update("biopsyDate", date)}
          placeholder={t("care.embryo_report.entry.select_date")}
        />
        <MoonDateField
          label={t("care.embryo_report.retrieval_date")}
          labelIcon={CalendarDays}
          required
          value={values.retrievalDate}
          onChange={(date) => update("retrievalDate", date)}
          placeholder={t("care.embryo_report.entry.select_date")}
        />
        <MoonFormField
          label={t("care.embryo_report.clinic")}
          labelIcon={FlaskConical}
          required
        >
          <FieldInput
            value={values.referringClinic}
            onChangeText={(value) => update("referringClinic", value)}
            placeholder={t("care.embryo_report.entry.clinic_placeholder")}
          />
        </MoonFormField>
        <MoonFormField
          label={t("care.embryo_report.physician")}
          labelIcon={UserRound}
          required
        >
          <FieldInput
            value={values.referringPhysician}
            onChangeText={(value) => update("referringPhysician", value)}
            placeholder={t("care.embryo_report.entry.physician_placeholder")}
          />
        </MoonFormField>
        <MoonFormField
          label={t("care.embryo_report.laboratory")}
          labelIcon={FlaskConical}
          required
        >
          <FieldInput
            value={values.laboratory}
            onChangeText={(value) => update("laboratory", value)}
            placeholder={t("care.embryo_report.entry.laboratory_placeholder")}
          />
        </MoonFormField>
        <MoonFormField
          label={t("care.embryo_report.indication")}
          labelIcon={FileText}
          required
        >
          <FieldInput
            value={values.indication}
            onChangeText={(value) => update("indication", value)}
            placeholder={t("care.embryo_report.entry.indication_placeholder")}
          />
        </MoonFormField>
        <MoonFormField
          label={t("care.embryo_report.sample_type")}
          labelIcon={FileText}
          required
        >
          <FieldInput
            value={values.sampleType}
            onChangeText={(value) => update("sampleType", value)}
            placeholder={t("care.embryo_report.entry.sample_type_placeholder")}
          />
        </MoonFormField>
        {!isEditing && (
          <OptionField
            label={t("care.embryo_report.entry.report_state")}
            value={values.reportState}
            options={REPORT_STATES.map((option) => ({
              value: option.value,
              label: t(option.labelKey),
            }))}
            onChange={(value) => update("reportState", value)}
            labelIcon={FileText}
          />
        )}
        <OptionField
          label={t("care.embryo_report.entry.test_type")}
          value={values.testType}
          options={TEST_TYPES.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
          }))}
          onChange={(value) => update("testType", value)}
          labelIcon={Dna}
        />
      </View>

      <View style={{ gap: 14 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <SectionTitle>
            {t("care.embryo_report.entry.embryo_results")}
          </SectionTitle>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("care.embryo_report.entry.add_embryo")),
                "care.embryo_report.entry.add_embryo",
              );
              setValues((current) => ({
                ...current,
                embryos: [...current.embryos, { ...EMPTY_EMBRYO }],
              }));
            }}
            accessibilityRole="button"
            accessibilityLabel={t("care.embryo_report.entry.add_embryo")}
            style={{ flexDirection: "row", alignItems: "center", gap: 5 }}
          >
            <Plus size={15} color={colors.warm} />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, fontWeight: "700", color: colors.warm }}
            >
              {t("care.embryo_report.entry.add_embryo")}
            </Text>
          </Pressable>
        </View>
        {values.embryos.length === 0 ? (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 12, lineHeight: 17, color: colors.textLight }}
          >
            {t("care.embryo_report.entry.no_embryo_results")}
          </Text>
        ) : null}
        {values.embryos.map((embryo, index) => (
          <View
            key={`embryo-${index}`}
            style={{
              gap: 12,
              padding: 14,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.08)",
              backgroundColor: "rgba(255,255,255,0.35)",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{ fontSize: 14, fontWeight: "700", color: colors.text }}
              >
                {t("care.embryo_report.entry.embryo_number", {
                  params: { number: String(index + 1) },
                })}
              </Text>
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("care.embryo_report.entry.remove_embryo")),
                    "care.embryo_report.entry.remove_embryo",
                  );
                  setValues((current) => ({
                    ...current,
                    embryos: current.embryos.filter(
                      (_, embryoIndex) => embryoIndex !== index,
                    ),
                  }));
                }}
                accessibilityRole="button"
                accessibilityLabel={t("care.embryo_report.entry.remove_embryo")}
              >
                <Trash2 size={16} color={colors.textLight} />
              </Pressable>
            </View>
            <MoonFormField
              label={t("care.embryo_report.entry.sample_id")}
              labelIcon={Hash}
              required
            >
              <FieldInput
                value={embryo.sampleId}
                onChangeText={(value) =>
                  updateEmbryo(index, { sampleId: value })
                }
              />
            </MoonFormField>
            <MoonFormField
              label={t("care.embryo_report.entry.embryo_number_field")}
              labelIcon={Hash}
              required
            >
              <FieldInput
                value={embryo.embryoNumber}
                onChangeText={(value) =>
                  updateEmbryo(index, {
                    embryoNumber: value.replace(/[^0-9]/g, ""),
                  })
                }
                keyboardType="number-pad"
              />
            </MoonFormField>
            <MoonFormField
              label={t("care.embryo_report.col_grade")}
              labelIcon={Dna}
            >
              <FieldInput
                value={embryo.grade}
                onChangeText={(value) => updateEmbryo(index, { grade: value })}
              />
            </MoonFormField>
            <MoonFormField
              label={t("care.embryo_report.entry.karyotype")}
              labelIcon={Dna}
              required
            >
              <FieldInput
                value={embryo.karyotype}
                onChangeText={(value) =>
                  updateEmbryo(index, { karyotype: value })
                }
              />
            </MoonFormField>
            <OptionField
              label={t("care.embryo_report.col_sex")}
              value={embryo.sex}
              options={SEX_OPTIONS}
              onChange={(value) => updateEmbryo(index, { sex: value })}
              labelIcon={UserRound}
            />
            <OptionField
              label={t("care.embryo_report.col_result")}
              value={embryo.result}
              options={RESULT_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              onChange={(value) => updateEmbryo(index, { result: value })}
              labelIcon={Dna}
            />
            <OptionField
              label={t("care.embryo_report.col_transfer")}
              value={embryo.transferRecommendation}
              options={TRANSFER_OPTIONS.map((option) => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              onChange={(value) =>
                updateEmbryo(index, { transferRecommendation: value })
              }
              labelIcon={FlaskConical}
            />
            <MoonFormField
              label={t("care.embryo_report.entry.biopsy_day")}
              labelIcon={Hash}
            >
              <FieldInput
                value={embryo.biopsyDay}
                onChangeText={(value) =>
                  updateEmbryo(index, {
                    biopsyDay: value.replace(/[^0-9]/g, ""),
                  })
                }
                keyboardType="number-pad"
              />
            </MoonFormField>
            <MoonDateField
              label={t("care.embryo_report.entry.embryo_biopsy_date")}
              labelIcon={CalendarDays}
              value={embryo.biopsyDate}
              onChange={(date) => updateEmbryo(index, { biopsyDate: date })}
              placeholder={t("care.embryo_report.entry.select_date")}
            />
            <MoonFormField
              label={t("care.embryo_report.entry.comments")}
              labelIcon={FileText}
            >
              <FieldInput
                value={embryo.comments}
                onChangeText={(value) =>
                  updateEmbryo(index, { comments: value })
                }
                multiline
              />
            </MoonFormField>
          </View>
        ))}
      </View>

      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, lineHeight: 17, color: colors.textLight }}
      >
        {t("care.embryo_report.entry.unknown_note")}
      </Text>

      {errorMessage ? (
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, lineHeight: 17, color: colors.error }}
        >
          {errorMessage}
        </Text>
      ) : null}

      <View style={{ flexDirection: "row", gap: 12 }}>
        <SoftSecondaryButton
          label={t("care.embryo_report.entry.cancel")}
          onPress={onCancel}
          flex={1}
        />
        <DialogPrimaryButton
          label={t(
            isEditing
              ? "care.embryo_report.entry.update"
              : "care.embryo_report.entry.save",
          )}
          onPress={() => onSubmit(values)}
          loading={isSubmitting}
          disabled={!canSubmit || isSubmitting}
          flex={1}
        />
      </View>
    </View>
  );
}
